import { isProductionEnv } from '@flow/env';

interface TConfig {
  /** Google reCAPTCHA site key used to verify user interactions. */
  readonly RECAPTCHA_SITE_KEY: string;

  /** Rollbar client-side access token. */
  readonly ROLLBAR_TOKEN: string;

  /** Base URL for the Oneflow API endpoints. */
  readonly ONEFLOW_API_URL: string;
}

/**
 * Config holds environment-specific configuration values and provides read-only access to them.
 * This class should only hold environment variables that are not secrets.
 */
class Config implements TConfig {
  // Hold the frozen singleton instance
  static #instance: Readonly<Config>;
  static #_ROLLBAR_TOKEN_DEVELOPMENT = '';
  static #_ROLLBAR_TOKEN_PRODUCTION = 'a858ed24ea4349eca3428eee1b24cbcd';

  // we use the same recaptcha site key for all environments
  static #_RECAPTCHA_SITE_KEY = '6LeKn70ZAAAAAHgqLAI0tHw-nVtU8K0zz3_9CBn_';

  constructor() {
    this.#initialize();
  }

  #initialize(): void {
    this.#RECAPTCHA_SITE_KEY = Config.#_RECAPTCHA_SITE_KEY;

    if (isProductionEnv()) {
      this.#ROLLBAR_TOKEN = Config.#_ROLLBAR_TOKEN_PRODUCTION;
    } else {
      this.#ROLLBAR_TOKEN = Config.#_ROLLBAR_TOKEN_DEVELOPMENT;
    }
  }

  #RECAPTCHA_SITE_KEY = '';
  /**
   * Google reCAPTCHA site key used to verify user interactions.
   */
  public get RECAPTCHA_SITE_KEY(): string {
    return this.#RECAPTCHA_SITE_KEY;
  }

  #ROLLBAR_TOKEN = '';

  /**
   * Rollbar client-side access token; returns an empty string in non-production environments.
   */
  public get ROLLBAR_TOKEN(): string {
    return this.#ROLLBAR_TOKEN;
  }

  /**
   * Base URL for the Oneflow API endpoints.
   */
  public get ONEFLOW_API_URL(): string {
    return '/api';
  }

  /**
   * Returns the singleton config instance. The instance is frozen to guarantee
   * runtime immutability and thus is typed as `Readonly<Config>`.
   */
  static getInstance(): Readonly<Config> {
    if (!Config.#instance) {
      Config.#instance = Object.freeze(new Config());
    }
    return Config.#instance;
  }
}

export const config = Config.getInstance();
